import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface Appointment {
	id: string;
	doula_user_id: string;
	doula_listing_id: string;
	appointment_date: string;
	status: string;
	duration_minutes: number;
	doula_user: {
		id: string;
		full_name: string;
		avatar_url: string;
		email: string;
	};
	doula_listing: {
		id: string;
		title: string;
		bio: string;
		location: string;
	};
}

interface DoulaSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	availableDoulas: Appointment[];
	onSelectDoula: (appointment: Appointment) => void;
	loading?: boolean;
}

const DoulaSelectionModal: React.FC<DoulaSelectionModalProps> = ({
	isOpen,
	onClose,
	availableDoulas,
	onSelectDoula,
	loading = false,
}) => {
	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800";
			case "accepted":
				return "bg-blue-100 text-blue-800";
			case "pending":
				return "bg-yellow-100 text-yellow-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-50 flex items-center justify-center p-4"
				>
					{/* Backdrop */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="absolute inset-0 bg-black/50 backdrop-blur-sm"
						onClick={onClose}
					/>

					{/* Modal */}
					<motion.div
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: 20 }}
						transition={{ type: "spring", damping: 25, stiffness: 300 }}
						className="relative w-full max-w-2xl max-h-[80vh] overflow-hidden"
					>
						<Card className="glass-card border-0 shadow-2xl bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
							<CardHeader className="border-b border-gray-200/50">
								<div className="flex items-center justify-between">
									<CardTitle className="text-xl font-karla flex items-center">
										<Users className="h-5 w-5 mr-2 text-primary-600" />
										Start New Conversation
									</CardTitle>
									<Button
										variant="ghost"
										size="sm"
										onClick={onClose}
										className="h-8 w-8 p-0 hover:bg-gray-100"
									>
										<X className="h-4 w-4" />
									</Button>
								</div>
							</CardHeader>

							<CardContent className="p-6">
								{loading ? (
									<div className="text-center py-8">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
										<p className="text-gray-600 font-karla">
											Loading available doulas...
										</p>
									</div>
								) : availableDoulas.length === 0 ? (
									<div className="text-center py-8">
										<Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
										<p className="text-gray-600 font-karla">
											No appointments found
										</p>
										<p className="text-sm text-gray-500 font-karla mt-2">
											Book an appointment with a doula to start messaging
										</p>
									</div>
								) : (
									<div className="space-y-3 max-h-[60vh] overflow-y-auto">
										{availableDoulas.map((appointment) => (
											<motion.div
												key={appointment.id}
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ duration: 0.3 }}
												onClick={() => onSelectDoula(appointment)}
												className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all duration-200 border border-transparent hover:border-primary-200 hover:shadow-md"
											>
												<div className="flex items-center space-x-4">
													<img
														src={
															appointment.doula_user?.avatar_url ||
															"/placeholder.svg"
														}
														alt={appointment.doula_user?.full_name || "Doula"}
														className="w-12 h-12 rounded-full object-cover shadow-sm"
													/>

													<div className="flex-1 min-w-0">
														<h4 className="font-semibold text-gray-900 font-karla truncate">
															{appointment.doula_user?.full_name || "Doula"}
														</h4>
														<p className="text-sm text-gray-600 font-karla truncate">
															{appointment.doula_listing?.title ||
																"Doula Service"}
														</p>
														<div className="flex items-center justify-between mt-2">
															<span className="text-xs text-gray-500 font-karla">
																{formatDate(appointment.appointment_date)}
															</span>
															<Badge
																variant="outline"
																className={`text-xs ${getStatusColor(appointment.status)}`}
															>
																{appointment.status}
															</Badge>
														</div>
													</div>
												</div>
											</motion.div>
										))}
									</div>
								)}
							</CardContent>
						</Card>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default DoulaSelectionModal;
